import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';
import { decimalString } from './schemas.ts';

/**
 * Recebimentos, contas de plataforma e bloqueios (ADR 0084, Entrega 5; D-02, D-03, D-21).
 * Só o crédito numa conta bancária é caixa realizado; o previsto entra na projeção na data
 * prevista. Nenhuma integração com as plataformas: tudo é lançado por alguém.
 */

export const PLATFORMS = [
  'stripe',
  'paddle',
  'hotmart',
  'shopify',
  'gamefound',
  'braintree',
  'square',
  'other',
] as const;
export type Platform = (typeof PLATFORMS)[number];

export const HOLD_KINDS = ['judicial', 'guarantee', 'reserve', 'other'] as const;
export const CASH_RECEIPT_ORIGINS = ['platform', 'customer', 'transfer', 'other'] as const;
export const CASH_RECEIPT_STATUSES = ['expected', 'received', 'cancelled', 'reversed'] as const;
export type CashReceiptStatus = (typeof CASH_RECEIPT_STATUSES)[number];

/**
 * Saldo disponível (D-02): o confirmado menos os bloqueios e reservas que valem na data. Sem saldo
 * confirmado, não há disponível (nunca zero por falta de informação).
 */
export function availableBalance(
  confirmed: string | null,
  holds: readonly { amount: string; startDate: string; endDate: string | null }[],
  date: string,
): { available: string | null; held: string } {
  let held = 0n;
  for (const h of holds) {
    if (h.startDate <= date && (h.endDate === null || h.endDate >= date))
      held += toScaled(h.amount);
  }
  return {
    available: confirmed === null ? null : fromScaled(toScaled(confirmed) - held),
    held: fromScaled(held),
  };
}

// ---------------------------------------------------------------------------
// Entradas da API
// ---------------------------------------------------------------------------

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
const positive = decimalString(15, 4).refine(
  (v) => toScaled(v) > 0n,
  'Informe um valor maior que zero',
);
const nonNegative = decimalString(15, 4).refine((v) => toScaled(v) >= 0n, 'Valor inválido');
const reason = z.string().trim().min(3).max(1000);
const optionalId = z.uuid().nullable().optional();
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const platformAccountInput = z.object({
  legalEntityId: z.uuid(),
  platform: z.enum(PLATFORMS),
  nickname: z.string().trim().min(2).max(100),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Moeda com 3 letras'),
  payoutAccountKey: optionalId,
  notes: optionalText(2000),
});

export const platformAccountUpdateInput = z.object({
  nickname: z.string().trim().min(2).max(100).optional(),
  payoutAccountKey: optionalId,
  notes: optionalText(2000),
});

export const balanceHoldInput = z
  .object({
    accountKey: z.uuid(),
    kind: z.enum(HOLD_KINDS),
    amount: positive,
    startDate: isoDate,
    endDate: isoDate.nullable().optional(),
    reason,
  })
  .refine((h) => !h.endDate || h.endDate >= h.startDate, {
    message: 'A data final vem depois do início',
    path: ['endDate'],
  });

const amounts = {
  grossAmount: positive,
  feeAmount: nonNegative.default('0'),
  withholdingAmount: nonNegative.default('0'),
};
const netPositive = (r: { grossAmount: string; feeAmount: string; withholdingAmount: string }) =>
  toScaled(r.grossAmount) - toScaled(r.feeAmount) - toScaled(r.withholdingAmount) > 0n;

export const cashReceiptInput = z
  .object({
    legalEntityId: z.uuid(),
    accountKey: z.uuid(),
    status: z.enum(['expected', 'received']),
    expectedDate: isoDate,
    effectiveDate: isoDate.nullable().optional(),
    ...amounts,
    origin: z.enum(CASH_RECEIPT_ORIGINS),
    partnerId: optionalId,
    payerName: optionalText(200),
    document: optionalText(100),
    description: z.string().trim().min(3).max(300),
    expenseTypeId: optionalId,
    classificationId: optionalId,
    costCenterId: optionalId,
    notes: optionalText(2000),
  })
  .refine((r) => r.status !== 'received' || !!r.effectiveDate, {
    message: 'Informe a data do crédito',
    path: ['effectiveDate'],
  })
  .refine(netPositive, {
    message: 'Taxas e retenções não podem passar do valor bruto',
    path: ['feeAmount'],
  })
  .refine((r) => !r.expenseTypeId === !r.classificationId, {
    message: 'Informe o Tipo de Gasto e o Tipo de Classificação juntos',
    path: ['classificationId'],
  });
export type CashReceiptInput = z.infer<typeof cashReceiptInput>;

/** Recebeu: a data do crédito e, se mudaram, os valores reais. */
export const cashReceiptReceiveInput = z
  .object({ effectiveDate: isoDate, ...amounts })
  .refine(netPositive, {
    message: 'Taxas e retenções não podem passar do valor bruto',
    path: ['feeAmount'],
  });

export const cashReceiptReasonInput = z.object({ reason });
